import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import _kebabCase from 'lodash/kebabCase'
import {
  PLAN_SUBSCRIPTION_MONTHLY,
  AUTHENTICATED_PLAN_SKUS,
  getCurrentPlanPrice,
  getActiveProductPlanRateId,
  isFourteenMonthPlan,
  PLAN_SUBSCRIPTION_LIVE,
} from 'services/plans'
import {
  EUR,
  CAD,
  renderSymbol as renderCurrencySymbol,
} from 'services/currency'
import { FR } from 'services/languages/constants'
import { scrollToTop } from 'services/dom'
import Button, { TYPES as BUTTON_TYPES } from 'components/Button'
import Icon, { ICON_TYPES } from 'components/Icon.v2'
import _replace from 'lodash/replace'
import { getPlanSimpleTitle } from 'components/PlanCardGrid/utils'
import { SLIDER_INACTIVE_INDEX } from 'components/Slider'

// Single Page Checkout - slider
const SLIDER_MOD_MAX_PROPS = 4
const SLIDER_GAIA_PLUS_INDEX = 2

function PlanCardValueProps (props) {
  const {
    valueProps,
    sliderMode,
    isGaiaPlus,
    activeSlide,
    staticText,
    setActiveSlide,
  } = props
  const [isPropsHidden, setIsPropsHidden] = useState(true)
  const hiddenPropsCount = valueProps.size - SLIDER_MOD_MAX_PROPS

  const renderProp = (index, prop, shouldHide) => (
    <div className={`plan-card__value-prop${shouldHide ? ' plan-card__value-prop--hidden' : ''}`} key={index}>
      <Icon className="plan-card__value-prop-check" type={ICON_TYPES.CHECK} />
      <span>{prop}</span>
    </div>
  )

  const handleViewMoreClick = () => {
    setIsPropsHidden(false)
    setActiveSlide(SLIDER_GAIA_PLUS_INDEX)
  }

  useEffect(() => {
    if (activeSlide !== SLIDER_GAIA_PLUS_INDEX) {
      setIsPropsHidden(true)
    }
    if (activeSlide === SLIDER_INACTIVE_INDEX || activeSlide === SLIDER_GAIA_PLUS_INDEX) {
      setIsPropsHidden(false)
    }
  }, [activeSlide])

  return (
    <div className="plan-card__value-props">
      {
        valueProps && List.isList(valueProps)
          ? valueProps.map((prop, index) => {
            const shouldHide = sliderMode
              && isGaiaPlus
              && index >= SLIDER_MOD_MAX_PROPS
              && isPropsHidden
            return renderProp(index, prop, shouldHide)
          })
          : null
      }
      {
        sliderMode && isGaiaPlus && isPropsHidden && (
          <Button
            className="plan-card__value-props-view-more"
            onClick={handleViewMoreClick}
            type={BUTTON_TYPES.LINK}
          >
          {
            // eslint-disable-next-line no-template-curly-in-string
            _replace(staticText.get('viewMore', ''), '${ hiddenPropsCount }', hiddenPropsCount)
          }
          </Button>
        )
      }
    </div>
  )
}

function PlanCard (props) {
  const {
    auth,
    plan = Map(),
    staticText = Map(),
    planClick,
    userPlanSubscriptionType,
    isAccountChangePlan,
    cancellationScheduled,
    shouldScrollToTop,
    userAccount = Map(),
    plans = Map(),
    useGraphQLData,
    currentPlan = Map(),
    sliderMode,
    activeSlide,
    setActiveSlide,
    index,
    userLanguage,
  } = props
  const plansData = plans.getIn(['data', 'plans'], List()) || List()
  const planType = plan.get('type')
  const title = isAccountChangePlan ? getPlanSimpleTitle({ planType, staticText }) : plan.get('title')
  const billed = plan.get('billed')
  const billedAbb = plan.get('billedAbb')
  const feature = plan.get('feature')
  const currencyIso = plan.get('currencyIso')
  const cost = plan.get('cost')
  const price = plan.get('price')
  const valueProps = plan.get('valueProps')
  const freeTrial = plan.get('freeTrial')
  const bestValue = plan.get('bestValue')
  // AP-16214 Black Friday Checkout - 2024
  const isFourteenMonth = isFourteenMonthPlan(plan.get('sku'))
  const bestValueCopy = isFourteenMonth ? staticText.get('blackFridayBestValue') : staticText.get('bestValue')
  const isMonthly = planType === PLAN_SUBSCRIPTION_MONTHLY
  const monthlyCostUnformatted = plan.get('monthlyCostUnformatted')
  const userCurrentRatePlanId = getActiveProductPlanRateId(userAccount)
  const userCurrentPlan = useGraphQLData ? currentPlan || Map() : plansData.find((p) => p.get('id') === userCurrentRatePlanId) || Map()
  const userCurrentSubscriptionPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan'], Map()) || Map()
  const userCurrentPlanSku = userCurrentPlan.get('sku')
  const planIsCurrentPlan = planType === userPlanSubscriptionType
  const planAlreadySelected = isAccountChangePlan
    && planIsCurrentPlan
    && !cancellationScheduled
  const userCurrentPlanCancelScheduled = isAccountChangePlan
    && planIsCurrentPlan
    && cancellationScheduled
  let userCurrentPlanCost = getCurrentPlanPrice(userAccount)
  const useCurrentPlanPrice = planAlreadySelected || userCurrentPlanCancelScheduled
  const userPlanIsLegacyStandardPlan = userCurrentPlanSku
    && AUTHENTICATED_PLAN_SKUS.includes(userCurrentPlanSku)
  const nextCharge = (userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'charges'], List()) || List()).last() || Map()
  const nextChargeAmount = nextCharge.get('price')
  const userInTrialSegment = userCurrentSubscriptionPlan.get('inTrialSegment')

  // Single Page Checkout - slider
  const selectedPlan = plans.get('selection') || null
  const isCurrentPlanSelected = selectedPlan
    && selectedPlan.get('sku') === plan.get('sku')
    && !!auth.get('jwt') // disable if the account is not created
  const isGaiaPlusPlan = planType === PLAN_SUBSCRIPTION_LIVE

  // If the person is in a legacy plan and this is their current plan
  if (isAccountChangePlan && planIsCurrentPlan && userPlanIsLegacyStandardPlan) {
    userCurrentPlanCost = nextChargeAmount
  }

  if (isAccountChangePlan && userInTrialSegment) {
    userCurrentPlanCost = cost
  }

  if (useGraphQLData && isAccountChangePlan) {
    userCurrentPlanCost = (userCurrentPlan.get('costs', List()) || List()).last()
  }

  // last ditch effort, just show a price
  if (isAccountChangePlan && !userCurrentPlanCost) {
    userCurrentPlanCost = (userCurrentPlan.get('costs', List()) || List()).last() || 0
  }

  const userNextBillAmountMonthlyCost = isMonthly
    ? userCurrentPlanCost
    : (parseFloat(userCurrentPlanCost) / 12).toFixed(2)

  const scrollToTopOnClick = () => {
    return new Promise((resolve) => {
      if (window.scrollY === 0) {
        resolve()
        // return so we don't get a listener set if the user doesn't scroll
        return
      }

      const onScroll = () => {
        if (window.scrollY === 0) {
          window.removeEventListener('scroll', onScroll)
          resolve()
        }
      }

      window.addEventListener('scroll', onScroll)
      scrollToTop()
    })
  }

  const onPlanSelectClicked = () => {
    if (shouldScrollToTop) {
      return scrollToTopOnClick().then(() => {
        planClick()
      })
    }
    // Single Page Checkout - slider
    if (sliderMode && activeSlide !== SLIDER_INACTIVE_INDEX) {
      setActiveSlide(index)
    }
    return planClick()
  }

  const renderPrice = (planPrice, abbreviation, showCents = true) => {
    if (!planPrice) {
      return null
    }

    const [roundCost, centsCost = '00'] = planPrice.split('.')
    const showCentsPrice = (showCents || parseInt(centsCost, 10) > 0)
    const showEurStylePrice = currencyIso === EUR || (currencyIso === CAD && userLanguage === FR)

    return (
      <>
        {`${!showEurStylePrice ? renderCurrencySymbol(currencyIso) : ''}${roundCost}${showEurStylePrice && !showCentsPrice ? ` ${renderCurrencySymbol(currencyIso)}` : ''}`}
        {showCentsPrice && <span className="plan-card__cents">{`${showEurStylePrice ? ',' : '.'}${centsCost}${showEurStylePrice ? ` ${renderCurrencySymbol(currencyIso)}` : ''}`}</span>}
        <span className="plan-card__abbreviation">/{abbreviation}</span>
      </>
    )
  }

  const getWrapperClassName = () => {
    const baseClassName = 'plan-card'
    const classNames = [baseClassName]

    if (bestValue) {
      classNames.push(`${baseClassName}--first`)
    }

    if (planAlreadySelected) {
      classNames.push(`${baseClassName}--current`)
    }

    // Single Page Checkout - slider
    if (sliderMode) {
      classNames.push(`${baseClassName}--slide`)
      if (activeSlide === SLIDER_GAIA_PLUS_INDEX) classNames.push(`${baseClassName}--expanded`)
    }

    return classNames.join(' ')
  }

  return (
    <div className={getWrapperClassName()}>
      {bestValue && <div className={`plan-card__best-value${isFourteenMonth ? ' plan-card__best-value--black-friday' : ''}`}>
        {bestValueCopy}
      </div>}
      <div className="plan-card__header">
        <div className={`plan-card__header-top${planAlreadySelected ? ' plan-card__header-top--current' : ''}`}>
          <div className="plan-card__title">{title}</div>
          <div className="plan-card__monthly-cost">
            {renderPrice(useCurrentPlanPrice ? userNextBillAmountMonthlyCost : monthlyCostUnformatted, staticText.get('monthAbbreviation'), true)}
          </div>
        </div>
        <div className={`plan-card__header-bottom${planAlreadySelected ? ' plan-card__header-bottom--current' : ''}`}>
          <div className="plan-card__billing-period">{billed}</div>
          <div className="plan-card__price">
            {
              !isMonthly
                && renderPrice(
                  useCurrentPlanPrice ? userCurrentPlanCost : cost,
                  billedAbb,
                  false,
                )
            }
          </div>
        </div>
      </div>
      {
        // Single Page Checkout - slider
        sliderMode
          ? <Button
            className={`plan-card__select-button${isCurrentPlanSelected ? ' plan-card__select-button--selected' : ''}`}
            onClick={isCurrentPlanSelected ? null : onPlanSelectClicked}
            data-action={`plan-select-${_kebabCase(planType)}`}
            type={isCurrentPlanSelected ? BUTTON_TYPES.SECONDARY : BUTTON_TYPES.PRIMARY}
            disabled={isCurrentPlanSelected}
          >
            {isCurrentPlanSelected
              ? <>
                <Icon className="plan-card__select-button-check" type={ICON_TYPES.CHECK} />
                {staticText.get('selectedPlan')}
              </>
              : staticText.get('selectPlan')
            }
          </Button>
          : <Button
            className="plan-card__select-button"
            onClick={planAlreadySelected ? null : onPlanSelectClicked}
            data-action={`plan-select-${_kebabCase(planType)}`}
            type={BUTTON_TYPES.PRIMARY}
            disabled={planAlreadySelected}
          >
            {planAlreadySelected ? staticText.get('currentPlan') : staticText.get('select')}
          </Button>
      }
      {
        feature && !isAccountChangePlan
          ? <div className="plan-card__feature">
              <div className={`plan-card__feature-title ${freeTrial ? 'plan-card__feature-title--teal' : ''}`}>
                {feature}
              </div>
              <div className="plan-card__feature-billing-info">
                {`${freeTrial || isFourteenMonth ? `${staticText.get('then')} ` : ''} ${price} ${billed}, ${staticText.get('cancelAnytime')}`}
              </div>
            </div>
          : <div className="plan-card__feature" />
      }
      {
        // Single Page Checkout - slider
        valueProps && <PlanCardValueProps
          valueProps={valueProps}
          sliderMode={sliderMode}
          isGaiaPlus={isGaiaPlusPlan}
          activeSlide={activeSlide}
          staticText={staticText}
          setActiveSlide={setActiveSlide}
        />
      }
    </div>
  )
}

PlanCard.propTypes = {
  plan: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  planClick: PropTypes.func.isRequired,
  currentPlan: ImmutablePropTypes.map,
  useGraphQLData: PropTypes.bool,
  sliderMode: PropTypes.bool,
  isActiveSlide: PropTypes.bool,
  setActiveSlide: PropTypes.func,
  index: PropTypes.number,
  userLanguage: PropTypes.string,
}

export default PlanCard
